import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DialogContent, DialogTitle, Divider, SvgIcon} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import {KeyboardArrowDown, Refresh} from "@mui/icons-material";
import useIsMounted from "../../../hooks/use-is-mounted";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import CopyToClipboard from "../../base/copy-to-clipboard";
import IconButton from "../../base/button/icon_button";
import {ReactComponent as Copy} from '../../../../assets/images/copy.svg'
import debounce from "lodash/debounce";
import Api from "../../../../core/services/api/api";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';

const formKeys = {
    expiry: 'expiry'
}

const initialLink = {
    expiry: '',
    url: '',
}

const schema = Yup.object().shape({
    [formKeys.expiry]: Yup.mixed()
        .nullable()
        .required(ValidateMessages.required)
        .validDate(ValidateMessages.incorrect)
        .disablePastDate(ValidateMessages.minDate("today"), 'day'),
})

const PartSupportExtraInfoLinksManagementDialog = ({open, setOpen, requestId}) => {
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [link, setLink] = useState(initialLink);
    const isMounted = useIsMounted();

    const initialValues = useMemo(() => ({
        [formKeys.expiry]: link.expiry ? DateUtils.serverToUTC(link.expiry) : undefined,
    }), [link.expiry])

    const validate = useMemo(() => MuiRFF.makeValidate(schema), []);
    const required = useMemo(() => MuiRFF.makeRequired(schema), []);

    /**
     * Fetches the current part support link
     * @type {(function(): Promise<void>)}
     */
    const getCurrentLink = useCallback(async () => {
        setLoading(true);
        const response = await Api.getPartSupportPublicLink({
            requestId: requestId,
        });
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setLink({
                expiry: response?.data?.expiryDateTime,
                url: response?.data?.link,
            });
        }
    }, [isMounted, requestId]);

    /**
     * With each change in the open prop:
     * - if the dialog is opened, calls the api for getting the current part support link
     */
    useEffect(() => {
        if (!open) {
            setLink(initialLink);
            setLoading(false);
            setUpdating(false);
            setAdvancedOpen(false);
            return;
        }
        getCurrentLink().then();
    }, [open, getCurrentLink])

    /**
     * Handles submission of driver's checklist
     */
    const updateLink = useCallback(async (values) => {
        if (!values[formKeys.expiry] || values[formKeys.expiry].isSame(DateUtils.serverToUTC(link.expiry), 'day'))
            return;
        setUpdating(true);
        const serverExpiryDateTime = values[formKeys.expiry] ? DateUtils.UTCToServer(values[formKeys.expiry]).format(DateUtils.withoutOffsetFormat) : null;
        const forApi = {
            shareLinkToken: link?.url?.split('/').pop(),
            expiryDateTime: serverExpiryDateTime,
            updatedProperties: [
                'ExpiryDateTime',
            ]
        }
        const response = await Api.updatePartSupportPublicLink(forApi);
        if (!isMounted())
            return;
        setUpdating(false);
        if (response?.resultFlag) {
            setLink(prevState => ({
                ...prevState,
                expiry: serverExpiryDateTime,
            }))
        }
    }, [isMounted, link.expiry, link?.url]);

    /**@type {function(form: formApi): void}*/
    const submitForm = debounce((form) => form?.submit(), 2000);

    /**
     * Creates a new part support link.
     * @type {(function(): Promise<void>)}
     */
    const createNewLink = useCallback(async () => {
        setLoading(true);
        const response = await Api.getPartSupportPublicLink({
            requestId: requestId,
        });
        if (!isMounted())
            return;
        setLoading(false);
        setAdvancedOpen(false);
        if (response?.resultFlag) {
            setLink({
                expiry: response?.data?.expiryDateTime,
                url: response?.data?.link,
            });
        }
    }, [isMounted, requestId])

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                maxWidth={'sm'}
                loading={loading}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Part Support Extra Info Link Management
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent>
                    {
                        loading
                            ? <LoadingLottieAnimation width={'90%'}/>
                            : <Col xs={12} className={'p-0 m-0'}>
                                <Form
                                    className='px-4 mt-3 w-100 d-flex flex-wrap w-100'
                                    onSubmit={updateLink}
                                    validate={validate}
                                    initialValues={initialValues}
                                    render={({form}) => {
                                        submitForm(form);
                                        return (
                                            <>
                                                <Col xs={12} className=' mb-4'>
                                                    <p className='text-xs text-color-1 mb-2'>
                                                        Shareable Link:
                                                    </p>
                                                    <Row
                                                        className={`background-secondary-color-3 rounded-xl no-gutters align-items-center justify-content-between px-3 py-2`}>
                                                        <p className='text-color-1 text-sm w-75'>
                                                            {link?.url ?? ''}
                                                        </p>
                                                        <div className={'d-flex align-items-center justify-content-end'}>
                                                            {
                                                                link?.url &&
                                                                <CopyToClipboard>
                                                                    {({copy}) =>
                                                                        <IconButton
                                                                            onPressed={() => copy(link?.url ?? '')}
                                                                            color={'secondary'}
                                                                            className={'copy-to-clipboard'}
                                                                        >
                                                                            <SvgIcon className='icon normal'
                                                                                     component={Copy}/>
                                                                        </IconButton>
                                                                    }
                                                                </CopyToClipboard>
                                                            }
                                                            {
                                                                link?.url &&
                                                                <IconButton
                                                                    onPressed={createNewLink}
                                                                    color={'secondary'}
                                                                    className={'ml-2'}
                                                                >
                                                                    <Refresh/>
                                                                </IconButton>
                                                            }
                                                        </div>
                                                    </Row>
                                                </Col>
                                                {
                                                    !advancedOpen
                                                        ? <Row
                                                            className='w-100 justify-content-center align-items-center mb-2 w-100'>
                                                            <Button
                                                                className='px-3 py-2 d-flex justify-content-center align-items-center'
                                                                variant={'text'}
                                                                color={'primary'}
                                                                onClick={() => setAdvancedOpen(true)}
                                                                disabled={updating}
                                                            >
                                                                <p>Advanced config</p>
                                                                <KeyboardArrowDown/>
                                                            </Button>
                                                        </Row>
                                                        : <Col xs={12} className='mb-4 mt-1'>
                                                            <MuiRFF.DatePicker
                                                                name={formKeys.expiry}
                                                                label={"Expire Date"}
                                                                required={required[formKeys.expiry]}
                                                                disablePast
                                                            />
                                                        </Col>
                                                }
                                            </>
                                        );
                                    }}
                                />
                            </Col>
                    }
                </DialogContent>

            </Dialog>
        </>
    )
}

export default PartSupportExtraInfoLinksManagementDialog;
